import { useCallback, useEffect, useState } from "react";
import {
  Activity as ActivityIcon,
  CalendarClock,
  Flame,
  Loader2,
  MapPinned,
  MessageCircle,
  Phone,
  Plus,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { STAGES, corridorLabel, leadWaLink, stageLabel } from "@/config/brand";
import { useAuth } from "@/context/AuthContext";
import { TempBadge, ago, inputCls, money, when } from "./ui";

const TABS = [
  { key: "details", label: "Details" },
  { key: "notes", label: "Notes" },
  { key: "schedule", label: "Schedule" },
  { key: "activity", label: "Activity" },
];

export default function LeadDrawer({ leadId, open, onClose, employees = [], onChanged }) {
  const { isAdmin, user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("details");
  const [note, setNote] = useState("");
  const [fu, setFu] = useState({ due_at: "", channel: "call", note: "" });
  const [visit, setVisit] = useState({ scheduled_at: "", project: "Wave City", pickup: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      const { data: res } = await api.get(`/leads/${leadId}`);
      setData(res);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [leadId]);

  useEffect(() => {
    if (open) {
      setTab("details");
      setData(null);
      load();
    }
  }, [open, load]);

  const lead = data?.lead;

  const patch = async (updates) => {
    setBusy(true);
    try {
      await api.patch(`/leads/${leadId}`, updates);
      toast.success("Lead updated");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const contact = async (channel) => {
    try {
      await api.post(`/leads/${leadId}/contact?channel=${channel}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/notes`, { text: note.trim() });
      setNote("");
      toast.success("Note saved");
      await load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const addFollowUp = async () => {
    if (!fu.due_at) return toast.error("Pick a date and time");
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/followups`, {
        due_at: new Date(fu.due_at).toISOString(),
        channel: fu.channel,
        note: fu.note,
      });
      setFu({ due_at: "", channel: "call", note: "" });
      toast.success("Follow-up scheduled");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const addVisit = async () => {
    if (!visit.scheduled_at) return toast.error("Pick a visit date and time");
    setBusy(true);
    try {
      await api.post(`/leads/${leadId}/visits`, {
        scheduled_at: new Date(visit.scheduled_at).toISOString(),
        project: visit.project,
        pickup: visit.pickup,
      });
      setVisit({ scheduled_at: "", project: "Wave City", pickup: "" });
      toast.success("Site visit booked");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        data-testid="lead-drawer"
        className="w-full overflow-y-auto border-line bg-white p-0 text-ink sm:max-w-lg"
      >
        <SheetTitle className="sr-only">{lead?.name || "Lead details"}</SheetTitle>
        <SheetDescription className="sr-only">
          Lead details, notes, schedule and activity history
        </SheetDescription>
        {!lead ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-brand" />
          </div>
        ) : (
          <div className="pb-8">
            <div className="sticky top-0 z-10 border-b border-line bg-white/95 px-5 pb-4 pt-6 backdrop-blur-xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 data-testid="drawer-lead-name" className="font-display text-2xl font-extrabold text-ink">
                    {lead.name}
                  </h2>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-body">
                    <span>{lead.phone}</span>
                    <span className="text-muted2">·</span>
                    <span>{corridorLabel(lead.corridor)}</span>
                    <TempBadge temp={lead.temperature} />
                  </div>
                </div>
                <span className="rounded-full border border-brand/30 bg-mint px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand">
                  {stageLabel(lead.status)}
                </span>
              </div>

              <div className="mt-4 flex gap-2">
                <a
                  href={`tel:${lead.phone}`}
                  onClick={() => contact("call")}
                  data-testid="drawer-call-btn"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full bg-brand py-2.5 text-sm font-bold text-white active:scale-[0.98]"
                >
                  <Phone className="h-4 w-4" /> Call
                </a>
                <a
                  href={leadWaLink(lead)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => contact("whatsapp")}
                  data-testid="drawer-whatsapp-btn"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full border border-brand/40 bg-cloud py-2.5 text-sm font-bold active:scale-[0.98]"
                >
                  <MessageCircle className="h-4 w-4 text-brand" /> WhatsApp
                </a>
              </div>

              <div className="mt-4 flex gap-1 rounded-lg border border-line bg-cloud p-1">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    data-testid={`drawer-tab-${t.key}`}
                    className={`flex-1 rounded-md py-1.5 text-xs font-medium transition-colors ${
                      tab === t.key ? "bg-mint text-brand" : "text-body hover:text-ink"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-5 px-5 pt-5">
              {tab === "details" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="col-span-2 block">
                      <span className="text-xs font-bold uppercase tracking-wide text-muted2">Pipeline stage</span>
                      <select
                        value={lead.status}
                        disabled={busy}
                        onChange={(e) => patch({ status: e.target.value })}
                        data-testid="drawer-status-select"
                        className={`${inputCls} mt-1.5`}
                      >
                        {STAGES.map((s) => (
                          <option key={s.key} value={s.key} className="bg-white">
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-bold uppercase tracking-wide text-muted2">Temperature</span>
                      <select
                        value={lead.temperature}
                        disabled={busy}
                        onChange={(e) => patch({ temperature: e.target.value })}
                        data-testid="drawer-temperature-select"
                        className={`${inputCls} mt-1.5`}
                      >
                        {["hot", "warm", "cold"].map((t) => (
                          <option key={t} value={t} className="bg-white">
                            {t}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-bold uppercase tracking-wide text-muted2">Deal value (₹)</span>
                      <input
                        type="number"
                        defaultValue={lead.deal_value || ""}
                        onBlur={(e) =>
                          e.target.value && Number(e.target.value) !== lead.deal_value
                            ? patch({ deal_value: Number(e.target.value) })
                            : null
                        }
                        placeholder="e.g. 7500000"
                        data-testid="drawer-deal-value-input"
                        className={`${inputCls} mt-1.5`}
                      />
                    </label>
                    {isAdmin && (
                      <label className="col-span-2 block">
                        <span className="text-xs font-bold uppercase tracking-wide text-muted2">Assigned advisor</span>
                        <select
                          value={lead.assigned_to || "unassigned"}
                          disabled={busy}
                          onChange={(e) => patch({ assigned_to: e.target.value })}
                          data-testid="drawer-assign-select"
                          className={`${inputCls} mt-1.5`}
                        >
                          <option value="unassigned" className="bg-white">Unassigned</option>
                          {employees.map((emp) => (
                            <option key={emp.id} value={emp.id} className="bg-white">
                              {emp.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {!isAdmin && !lead.assigned_to && (
                      <button
                        onClick={() => patch({ assigned_to: user.id })}
                        disabled={busy}
                        data-testid="drawer-claim-btn"
                        className="col-span-2 rounded-xl bg-brand py-2.5 text-xs font-bold text-white disabled:opacity-60"
                      >
                        Claim this lead
                      </button>
                    )}

                  </div>

                  <div className="rounded-xl border border-line bg-cloud p-4">
                    <div className="grid grid-cols-2 gap-y-3 text-xs">
                      {[
                        ["Budget", lead.budget || "—"],
                        ["Property type", lead.property_type || "—"],
                        ["Timeline", lead.timeline || "—"],
                        ["Source", (lead.source || "").replace("_", " ")],
                        ["Advisor", lead.assigned_to_name || "Unassigned"],
                        ["Deal value", money(lead.deal_value)],
                        ["Created", when(lead.created_at)],
                        ["Last contact", lead.last_contacted_at ? ago(lead.last_contacted_at) : "never"],
                      ].map(([k, v]) => (
                        <div key={k}>
                          <div className="text-xs font-semibold uppercase tracking-wide text-muted2">{k}</div>
                          <div className="mt-0.5 text-ink2">{v}</div>
                        </div>
                      ))}
                    </div>
                    {lead.message && (
                      <p className="mt-4 border-t border-line pt-3 text-xs italic leading-relaxed text-body">
                        “{lead.message}”
                      </p>
                    )}
                  </div>

                  <div className="rounded-xl border border-brand/25 bg-mint p-4">
                    <div className="flex items-center gap-2">
                      <Target className="h-3.5 w-3.5 text-brand" />
                      <span className="text-xs font-bold uppercase tracking-wide text-brand">
                        Google Ads attribution
                      </span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-y-2.5 text-xs">
                      {[
                        ["Campaign", lead.attribution?.utm_campaign],
                        ["Source / Medium", [lead.attribution?.utm_source, lead.attribution?.utm_medium].filter(Boolean).join(" / ")],
                        ["Keyword", lead.attribution?.utm_term],
                        ["Creative", lead.attribution?.utm_content],
                        ["Device", lead.attribution?.device],
                        ["GCLID", lead.attribution?.gclid],
                      ].map(([k, v]) => (
                        <div key={k} className={k === "GCLID" ? "col-span-2" : ""}>
                          <div className="text-xs font-semibold uppercase tracking-wide text-muted2">{k}</div>
                          <div className="mt-0.5 break-all text-[11px] text-ink2">{v || "—"}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {tab === "notes" && (
                <>
                  <div>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      placeholder="What did the client say?"
                      data-testid="drawer-note-input"
                      className={inputCls}
                    />
                    <button
                      onClick={addNote}
                      disabled={busy}
                      data-testid="drawer-add-note-btn"
                      className="mt-2 flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white disabled:opacity-60"
                    >
                      <Plus className="h-3.5 w-3.5" /> Add note
                    </button>
                  </div>
                  <div className="space-y-3" data-testid="drawer-notes-list">
                    {data.notes.length === 0 && <p className="text-xs text-muted2">No notes yet.</p>}
                    {data.notes.map((n) => (
                      <div key={n.id} className="rounded-xl border border-line bg-cloud p-3.5">
                        <p className="text-sm leading-relaxed text-ink2">{n.text}</p>
                        <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted2">
                          {n.user_name} · {ago(n.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {tab === "schedule" && (
                <>
                  <div className="rounded-xl border border-line bg-cloud p-4">
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-3.5 w-3.5 text-brand" />
                      <span className="text-xs font-bold uppercase tracking-wide text-body">
                        Schedule follow-up
                      </span>
                    </div>
                    <div className="mt-3 space-y-2">
                      <input
                        type="datetime-local"
                        value={fu.due_at}
                        onChange={(e) => setFu({ ...fu, due_at: e.target.value })}
                        data-testid="drawer-followup-datetime"
                        className={inputCls}
                      />
                      <select
                        value={fu.channel}
                        onChange={(e) => setFu({ ...fu, channel: e.target.value })}
                        data-testid="drawer-followup-channel"
                        className={inputCls}
                      >
                        {["call", "whatsapp", "meeting"].map((c) => (
                          <option key={c} value={c} className="bg-white">{c}</option>
                        ))}
                      </select>
                      <button
                        onClick={addFollowUp}
                        disabled={busy}
                        data-testid="drawer-add-followup-btn"
                        className="w-full rounded-full bg-brand py-2.5 text-xs font-bold text-white disabled:opacity-60"
                      >
                        Set follow-up
                      </button>
                    </div>
                    <div className="mt-4 space-y-2" data-testid="drawer-followups-list">
                      {data.followups.map((f) => (
                        <div key={f.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-xs">
                          <span className="text-ink2">
                            {when(f.due_at)} · {f.channel}
                          </span>
                          <span className={f.status === "done" ? "text-emerald-600" : "text-brand"}>{f.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-line bg-cloud p-4">
                    <div className="flex items-center gap-2">
                      <MapPinned className="h-3.5 w-3.5 text-brand" />
                      <span className="text-xs font-bold uppercase tracking-wide text-body">
                        Book site visit
                      </span>
                    </div>
                    <div className="mt-3 space-y-2">
                      <input
                        type="datetime-local"
                        value={visit.scheduled_at}
                        onChange={(e) => setVisit({ ...visit, scheduled_at: e.target.value })}
                        data-testid="drawer-visit-datetime"
                        className={inputCls}
                      />
                      <select
                        value={visit.project}
                        onChange={(e) => setVisit({ ...visit, project: e.target.value })}
                        data-testid="drawer-visit-project"
                        className={inputCls}
                      >
                        {["Wave City", "Aditya World City", "Siddharth Vihar"].map((p) => (
                          <option key={p} value={p} className="bg-white">{p}</option>
                        ))}
                      </select>
                      <input
                        value={visit.pickup}
                        onChange={(e) => setVisit({ ...visit, pickup: e.target.value })}
                        placeholder="Pickup point (optional)"
                        data-testid="drawer-visit-pickup"
                        className={inputCls}
                      />
                      <button
                        onClick={addVisit}
                        disabled={busy}
                        data-testid="drawer-add-visit-btn"
                        className="w-full rounded-full border border-brand/40 bg-mint py-2.5 text-xs font-bold text-brand disabled:opacity-60"
                      >
                        Book visit
                      </button>
                    </div>
                    <div className="mt-4 space-y-2" data-testid="drawer-visits-list">
                      {data.visits.map((v) => (
                        <div key={v.id} className="flex items-center justify-between rounded-lg border border-line px-3 py-2 text-xs">
                          <span className="text-ink2">
                            {when(v.scheduled_at)} · {v.project}
                          </span>
                          <span className={v.status === "completed" ? "text-emerald-600" : "text-brand"}>{v.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {tab === "activity" && (
                <div className="space-y-0" data-testid="drawer-activity-list">
                  {data.activities.map((a) => (
                    <div key={a.id} className="flex gap-3 border-l border-line pb-5 pl-4 last:pb-0">
                      <span className="absolute -ml-[21px] mt-1 h-2 w-2 rounded-full bg-brand" />
                      <div>
                        <p className="text-sm text-ink2">{a.message}</p>
                        <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-muted2">
                          {a.user_name} · {when(a.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
